import React, { useState } from 'react';
import ChartRenderer from './ChartRenderer';
import { QueryResult } from '../../types/analytics';

interface QueryResultsProps {
  result: QueryResult;
}

const QueryResults: React.FC<QueryResultsProps> = ({ result }) => {
  const [showSQL, setShowSQL] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'chart'>('table');
  const [chartType, setChartType] = useState<'bar' | 'line' | 'pie'>('bar');

  // Determine if data is suitable for charting (has numeric values)
  const canChart = result.results.length > 0 && result.results.some(row =>
    Object.values(row).some(val => typeof val === 'number')
  );

  return (
    <div className="space-y-4 bg-gray-800 border border-gray-700 rounded-lg p-6">
      {/* Explanation */}
      {result.explanation && (
        <div className="text-gray-300 leading-relaxed">
          <p>{result.explanation}</p>
          <p className="text-sm text-gray-400 mt-2">
            {result.row_count} {result.row_count === 1 ? 'row' : 'rows'} returned
          </p>
        </div>
      )}

      {/* View Mode Toggle */}
      {canChart && result.results.length > 0 && (
        <div className="flex items-center gap-4 border-b border-gray-700 pb-4">
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('table')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'table'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Table
              </div>
            </button>
            <button
              onClick={() => setViewMode('chart')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'chart'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Chart
              </div>
            </button>
          </div>

          {/* Chart Type Selector (only shown when in chart mode) */}
          {viewMode === 'chart' && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-gray-400">Chart type:</span>
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value as 'bar' | 'line' | 'pie')}
                className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-1.5 text-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="bar">Bar Chart</option>
                <option value="line">Line Chart</option>
                <option value="pie">Pie Chart</option>
              </select>
            </div>
          )}
        </div>
      )}

      {/* Data Display */}
      <ChartRenderer
        chartType={viewMode === 'table' ? 'table' : chartType}
        data={result.results}
        xKey={undefined}
        yKey={undefined}
      />

      {/* Collapsible SQL Query */}
      <div className="border-t border-gray-700 pt-4">
        <button
          onClick={() => setShowSQL(!showSQL)}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-300 transition-colors"
        >
          <svg
            className={`w-4 h-4 transform transition-transform ${showSQL ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          {showSQL ? 'Hide SQL Query' : 'Show SQL Query'}
        </button>

        {showSQL && (
          <pre className="bg-gray-900 text-gray-300 p-4 rounded-lg mt-3 overflow-x-auto text-sm">
            <code>{result.sql}</code>
          </pre>
        )}
      </div>
    </div>
  );
};

export default QueryResults;
